import type { UserNotificationPreferenceDto } from '@up2stay/up2promo-api-client';
import { Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { NotificationPreferencesTable } from '@/core/components/notifications/notification-preferences-table';
import type { NotificationPreferenceTableRow } from '@/core/components/notifications/notification-preferences-table.types';
import { PushSettingsBanner } from '@/core/components/notifications/push-settings-banner';
import { SmsPhoneSettingsBanner } from '@/core/components/notifications/sms-phone-settings-banner';
import { SettingsPageShell } from '@/core/components/settings/settings-page-shell';
import { useNotificationCapabilities } from '@/core/hooks/use-notification-capabilities';
import { useUserNotificationPreferences } from '@/core/hooks/use-notification-preferences';
import {
  resolveNotificationTypeDescription,
  resolveNotificationTypeLabel,
} from '@/core/lib/notification-type-i18n';
import { useAuthStore } from '@/core/store/auth.store';

type UserNotificationPreferencesPanelProps = {
  dict: {
    myPreferencesTitle: string;
    myPreferencesDescription: string;
    channels: {
      email: string;
      inApp: string;
      sms: string;
      push: string;
      pushDenied: string;
      pushUnavailable: string;
    };
    pushPrompt: {
      bannerTitle: string;
      descriptionPrompt: string;
      descriptionDenied: string;
      enable: string;
      openSystemSettings: string;
    };
    table: {
      allChannels: string;
      domains: Record<string, string | undefined>;
    };
    types: Record<string, { label?: string; description?: string } | undefined>;
    smsPhone?: {
      title: string;
      description: string;
      action: string;
    };
  };
};

function toTableRows(
  rows: UserNotificationPreferenceDto[],
  typesDict: UserNotificationPreferencesPanelProps['dict']['types'],
): NotificationPreferenceTableRow[] {
  return rows.map((row) => ({
    typeId: row.type.id,
    domain: row.type.domain,
    label: resolveNotificationTypeLabel(row.type.id, row.type.label, typesDict),
    description: resolveNotificationTypeDescription(
      row.type.id,
      row.type.description,
      typesDict,
    ),
    channels: row.channels,
  }));
}

export function UserNotificationPreferencesPanel({
  dict,
}: UserNotificationPreferencesPanelProps) {
  const { data, isLoading, savePreferences, refetch } =
    useUserNotificationPreferences();
  const { data: capabilities, isLoading: capabilitiesLoading } =
    useNotificationCapabilities();
  const phoneVerified = useAuthStore((state) => state.user?.phoneVerified);
  const pushFeatureEnabled = capabilities?.pushEnabled === true;
  const [editedRows, setEditedRows] = useState<
    UserNotificationPreferenceDto[] | null
  >(null);
  const rows = useMemo(() => editedRows ?? data ?? [], [editedRows, data]);

  const tableRows = useMemo(
    () => toTableRows(rows, dict.types),
    [rows, dict.types],
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <SettingsPageShell>
      <PushSettingsBanner
        pushFeatureEnabled={pushFeatureEnabled}
        dict={{
          title: dict.pushPrompt.bannerTitle,
          descriptionPrompt: dict.pushPrompt.descriptionPrompt,
          descriptionDenied: dict.pushPrompt.descriptionDenied,
          enable: dict.pushPrompt.enable,
          openSystemSettings: dict.pushPrompt.openSystemSettings,
        }}
        onEnabled={() => {
          setEditedRows(null);
          void refetch();
        }}
      />

      {phoneVerified !== true && dict.smsPhone ? (
        <SmsPhoneSettingsBanner
          title={dict.smsPhone.title}
          description={dict.smsPhone.description}
          actionLabel={dict.smsPhone.action}
        />
      ) : null}

      {rows.length === 0 ? null : (
        <NotificationPreferencesTable
          rows={tableRows}
          showPush={!capabilitiesLoading && pushFeatureEnabled}
          dict={{
            allChannels: dict.table.allChannels,
            domains: dict.table.domains,
            channels: dict.channels,
            pushDenied: dict.channels.pushDenied,
            pushUnavailable: dict.channels.pushUnavailable,
          }}
          onRowsChange={(nextTableRows) => {
            setEditedRows((prev) => {
              const base = prev ?? data ?? [];
              return base.map((row) => {
                const updated = nextTableRows.find(
                  (item) => item.typeId === row.type.id,
                );
                return updated ? { ...row, channels: updated.channels } : row;
              });
            });
          }}
          onPersist={async (nextTableRows) => {
            const payload = nextTableRows.map((row) => ({
              typeId: row.typeId,
              channels: row.channels,
            }));

            setEditedRows((prev) => {
              const base = prev ?? data ?? [];
              return base.map((row) => {
                const updated = nextTableRows.find(
                  (item) => item.typeId === row.type.id,
                );
                return updated ? { ...row, channels: updated.channels } : row;
              });
            });

            await savePreferences(payload);
            setEditedRows(null);
          }}
        />
      )}
    </SettingsPageShell>
  );
}
